/**
 * CARD MODE economy + the evolving hand, ported from the bskyrts card game.
 *
 * You hold a small hand of tank cards. Each card is a TANK TYPE at an upgrade LEVEL;
 * deploying it costs cash (which you earn from kills) and spawns an ally tank boosted
 * by that level. The hand is "always evolving": on a per-card goalpost timer each card
 * re-rolls its level — biased toward what your current cash can afford — so a rich bank
 * surfaces stronger tanks. Deploying a type repeatedly escalates that type's cost.
 *
 * Pure logic (no DOM / Three): the game drives update()/buy()/earn() and renders the
 * hand from `slots`/`cash`.
 */

import { CARD_MODE, CARD_TYPES, CARD_BASE_COST, TANK_TYPES, mechaEligible } from "../config";

const MECHA_CARD_CHANCE = 0.12; // chance an eligible card draws as a mecha
const MECHA_CARD_PRICE = 1.8;   // mecha cards cost more (a walker is a powerhouse)

export interface TankCard {
  type: string;     // tank type id (e.g. "red")
  level: number;    // upgrade level 1..CARD_MODE.maxLevel
  priceMult: number; // per-card discount/markup (0.55..1.6)
  gpTimer: number;  // seconds until this card's level re-rolls ("goalpost")
  warn: boolean;    // true in the last ~3.5s before a re-roll (HUD pulses it)
  mecha?: boolean;  // this card deploys the tank on the tall 4-legged mecha walker body
}

/** Per-card discount / neutral / markup roll (drives the price badge). */
function rollPriceMult(): number {
  const r = Math.random();
  if (r < 0.3) return 0.55 + Math.random() * 0.3;  // −15%..−45% (a deal)
  if (r < 0.62) return 1;                            // face value
  return 1.2 + Math.random() * 0.4;                  // +20%..+60% (a markup)
}
const gpInterval = (): number => 10 + Math.random() * 10; // re-roll every 10–20s

export class CardDeck {
  cash: number;
  slots: TankCard[];
  /** how many allies of each type are currently fielded — set by the game; escalates cost. */
  fielded: Record<string, number> = {};

  constructor() {
    this.cash = CARD_MODE.startCash;
    this.slots = Array.from({ length: CARD_MODE.coreSlots }, () => this.roll());
  }

  /** Cost to deploy `type` at `level` with a given price multiplier (+ field escalation). */
  costOf(type: string, level: number, priceMult: number): number {
    const base = CARD_BASE_COST[type] ?? 50;
    const lvlMul = 1 + 0.6 * (level - 1);
    const fieldMul = Math.pow(1.4, this.fielded[type] ?? 0); // each fielded copy makes the next pricier
    return Math.ceil(base * lvlMul * priceMult * fieldMul);
  }
  cost(c: TankCard): number { return Math.ceil(this.costOf(c.type, c.level, c.priceMult) * (c.mecha ? MECHA_CARD_PRICE : 1)); }
  canAfford(c: TankCard): boolean { return this.cash >= this.cost(c); }
  /** Is any card in hand affordable right now? (used for the "out of options" lose check) */
  anyAffordable(): boolean { return this.slots.some((c) => this.canAfford(c)); }

  /** Highest level of `type` the current cash could buy at face value — the drift target. */
  private maxAffordableLevel(type: string): number {
    let lvl = 1;
    while (lvl < CARD_MODE.maxLevel && this.costOf(type, lvl + 1, 1) <= this.cash) lvl++;
    return lvl;
  }
  /** Level skewed toward affordability: broke → mostly 1–2, rich → trends to the cap. */
  private rollLevel(type: string): number {
    const cap = this.maxAffordableLevel(type);
    const skew = cap >= 4 ? 0.8 : 1.7; // rich = flatter (high levels likely); poor = steep (low)
    return Math.max(1, Math.min(cap, 1 + Math.floor(Math.pow(Math.random(), skew) * cap)));
  }
  /** Pick a type, biased AWAY from types already in hand (variety). */
  private biasedType(): string {
    const held = new Set(this.slots?.map((c) => c.type));
    const pool: string[] = [];
    for (const id of CARD_TYPES) { pool.push(id); if (!held?.has(id)) pool.push(id); }
    return pool[Math.floor(Math.random() * pool.length)];
  }
  private roll(): TankCard {
    const type = this.biasedType();
    const mecha = mechaEligible(TANK_TYPES[type]) && Math.random() < MECHA_CARD_CHANCE;
    return { type, level: this.rollLevel(type), priceMult: rollPriceMult(), gpTimer: gpInterval(), warn: false, mecha };
  }

  earn(n: number): void { this.cash += n; }

  /** Spend on slot `i` and replace it with a fresh (evolving) card. Returns the bought card. */
  buy(i: number): TankCard | null {
    const c = this.slots[i];
    if (!c || !this.canAfford(c)) return null;
    this.cash -= this.cost(c);
    this.slots[i] = this.roll();
    return c;
  }

  /** Tick the evolving hand: on each card's goalpost timer it drifts — usually re-rolling
   *  its LEVEL toward what you can now afford, but ~1 in 3 times swapping to a fresh TYPE
   *  too (so the hand churns and an all-expensive hand can't soft-lock a broke player). */
  update(dt: number): void {
    for (let i = 0; i < this.slots.length; i++) {
      const c = this.slots[i];
      c.gpTimer -= dt;
      c.warn = c.gpTimer < 3.5;
      if (c.gpTimer <= 0) {
        if (Math.random() < 0.35) {
          this.slots[i] = this.roll(); // a whole fresh card drifts in (type + level)
        } else {
          c.level = this.rollLevel(c.type); // just re-roll the level toward affordability
          c.priceMult = rollPriceMult();
          c.gpTimer = gpInterval();
          c.warn = false;
        }
      }
    }
  }
}
